import React, { useState } from 'react';
import { Badge, Button, Spinner } from 'react-bootstrap';
import AiInstructionsService, { apiFieldMessages, isNotDeployed } from '../../../Services/AiInstructionsService';

/**
 * WHAT THE MODEL ACTUALLY RECEIVES, word for word, read-only.
 *
 * ── WHY THIS EXISTS ──
 * The owner asked three times where the prompts were, and the screen was not
 * lying to them: nothing is published for any scope, so the guidance body IS
 * empty. But the prompt the agent runs on was not empty — it was invisible.
 * Every scope has a code-owned prompt that no publish can relax, the admin's
 * body is appended BEHIND it inside a fence, and none of that appeared
 * anywhere in the dashboard. "I still don't see the prompts" was a correct
 * description of a real gap, not a misreading of the screen.
 *
 * ── THE TWO HALVES ARE NOT EQUAL, AND THE PANEL SAYS SO ──
 * `composeSystemPrompt` puts the code-owned prompt FIRST, then a preamble
 * telling the model the block that follows is reference material with no
 * authority over what came before, then the body inside
 * `<operator_guidance scope=… version=…>`. So the division is: the code says
 * what must be true of any output whatever the office's policy is; the
 * published body says the policy. One is a deploy, the other is this screen.
 * Showing them as one undifferentiated blob would invite an admin to believe
 * they can edit the first half.
 *
 * ── IT FETCHES, IT DOES NOT COMPOSE ──
 * Nothing here holds or assembles prompt text. The dashboard must not carry a
 * copy of a production prompt — it would be a second copy to keep in step,
 * and for a prompt "out of step" means this screen confidently showing an
 * admin rules the model is not running under. There is also no scope → prompt
 * mapping anywhere to copy: each consumer holds its own and hands it to
 * `composeSystemPrompt`. See `getComposedPrompt`, which is the seam.
 *
 * ── ON DEMAND, NOT ON LOAD ──
 * One request when somebody asks, the same shape as reading a history
 * version: a composed prompt is the code prompt plus a body capped at 8–20 KB,
 * and three of them on every page load would be a cost paid by everyone who
 * came to edit one sentence.
 */

/** "—" rather than a blank, so an absent fact reads as absent. */
const or = (value) => (value === 0 || value ? value : '—');

function ComposedPrompt({
  scope,
  scopeLabel,
  publishedVersion,
  draftVersion,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [error, setError] = useState('');
  const [unbuilt, setUnbuilt] = useState(false);
  // Which version was asked for: `null` means "whatever is live".
  const [asked, setAsked] = useState(null);

  const load = async (version) => {
    setLoading(true);
    setError('');
    setUnbuilt(false);
    setPrompt(null);
    setAsked(version ?? null);
    setOpen(true);
    try {
      const result = await AiInstructionsService.getComposedPrompt({ scope, version });
      setPrompt(result);
    } catch (err) {
      if (isNotDeployed(err)) setUnbuilt(true);
      else {
        setError(apiFieldMessages(err).join('; ')
          || err?.response?.data?.message
          || err?.message
          || 'Could not read the prompt');
      }
    } finally {
      setLoading(false);
    }
  };

  const hasGuidance = !!prompt && typeof prompt.guidance === 'string' && prompt.guidance.trim();

  return (
    <section className="ai-panel ai-prompt">
      <div className="ai-panel-title">
        <i className="fa fa-file-lines me-2" aria-hidden="true" />
        The prompt the model receives
      </div>

      <p className="ai-panel-note">
        {'Every scope runs on a prompt written in code that no published'
         + ' guidance can relax. What you publish here is appended BEHIND it,'
         + ' fenced and introduced as reference material. This shows the two'
         + ' together, exactly as the model is handed them.'}
      </p>

      <div className="ai-prompt-actions">
        <Button
          variant="outline-secondary"
          disabled={loading || !scope}
          onClick={() => load(undefined)}
        >
          <i className="fa fa-tower-broadcast me-2" aria-hidden="true" />
          {publishedVersion
            ? `Show what is live (v${publishedVersion})`
            : 'Show what is running now'}
        </Button>
        {/* The draft's composed prompt is the honest "what am I about to
            publish" read, and it is the same stored-version shape the history
            read already uses. Unsaved text has no version, so there is
            nothing to ask for — the same gate as publish and the dry run. */}
        {draftVersion && (
          <Button
            variant="outline-primary"
            disabled={loading}
            onClick={() => load(draftVersion)}
          >
            {`Show what v${draftVersion} would send`}
          </Button>
        )}
      </div>

      {loading && (
        <p className="ai-panel-note mb-0">
          <Spinner animation="border" size="sm" className="me-2" />
          Reading the prompt…
        </p>
      )}

      {error && <p className="ai-decision-error mb-0">{error}</p>}

      {unbuilt && (
        <div className="ai-panel ai-panel--warn ai-prompt-unbuilt">
          <div className="ai-panel-title">
            <i className="fa fa-screwdriver-wrench me-2" aria-hidden="true" />
            The prompt is not served to the dashboard yet
          </div>
          <p className="mb-0">
            {'The screen is asking for '}
            <code>GET /ai/instructions/prompt</code>
            {' and the API has no such route yet. The prompt is deliberately'
             + ' NOT copied into the dashboard: a second copy of a production'
             + ' prompt goes stale silently, and this screen would then be'
             + ' showing rules the model is not running under. Nothing else on'
             + ' this screen is affected.'}
          </p>
        </div>
      )}

      {open && prompt && !loading && (
        <div className="ai-prompt-body">
          <div className="ai-prompt-head">
            <Badge bg="secondary">
              {asked === null ? 'Live' : `Version ${asked}`}
            </Badge>
            <span className="ai-prompt-meta">
              {hasGuidance
                ? `code-owned rules + your guidance (v${or(prompt.version)})`
                : 'code-owned rules only'}
            </span>
          </div>

          <span className="ai-panel-label">
            Written in code — a deploy changes this, not this screen
          </span>
          <pre className="ai-decision-json ai-prompt-code" aria-label="Code-owned prompt">
            {prompt.code_owned || 'The server sent no code-owned prompt.'}
          </pre>

          <span className="ai-panel-label">Your published guidance</span>
          {hasGuidance ? (
            <pre className="ai-decision-json ai-prompt-guidance" aria-label="Published guidance in the prompt">
              {prompt.guidance}
            </pre>
          ) : (
            /* The answer to the question that was asked three times. It is
               not an error and not an empty panel: it is what the agent is
               running on, which is the code-owned rules by themselves. */
            <p className="ai-panel-note ai-prompt-empty">
              {`Nothing is published for ${scopeLabel || scope}, so the agent is`
               + ' running on the code-owned rules above and nothing else. That'
               + ' is why this half is blank — publish a version and it appears'
               + ' here, fenced, under its version number.'}
            </p>
          )}

          <span className="ai-panel-label">
            The whole thing, as the model receives it
          </span>
          <pre className="ai-decision-json ai-prompt-composed" aria-label="Composed system prompt">
            {prompt.composed}
          </pre>

          <Button size="sm" variant="link" className="p-0" onClick={() => setOpen(false)}>
            Hide the prompt
          </Button>
        </div>
      )}
    </section>
  );
}

export default ComposedPrompt;
